import { useEffect, useRef, useState } from 'react';
import type { GlobalError } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { mapApiErrorCode } from '@/errorCodes/helpers';

import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import type { ModalProps } from '@/components/Modal';
import { Modal } from '@/components/Modal';
import { SectionHeader } from '@/components/SectionHeader';
import type {
  OrderAppCtaType,
  OrderAppError,
  OrderAppMetadataInput,
} from '@/graphql/orderApp/api';
import type {
  CheckoutLineFragment,
  ProductVariantLineFragment,
} from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { ENGRAVING_VARIANT_ID } from '@/lib/const';
import {
  getMessageEngravingPreviewValue,
  type MessageEngravingValidationError,
  validateMessageEngraving,
} from '@/lib/engraving';
import { EVENT_NAME, popinTitle } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import { commonMessages } from '@/messages/common';
import { engravingMessages } from '@/messages/engraving';
import { formMessages } from '@/messages/forms';
import { serviceMessages } from '@/messages/service';

import { Button } from '../Button';
import { Flex } from '../Flex';
import { isErrorNotification } from '../Header/components';
import { ErrorMessage, InputErrorMessage } from '../InputErrorMessage';
import { Radio } from '../Radio';
import { getFormattedMessage } from '../ServerErrors/ServerErrors';
import { EngravingImage } from './components/EngravingImage';
import { EngravingInputs } from './components/EngravingInputs';
import {
  parseCoordinates,
  sanitizeCoordinates,
  validateEngravingDate,
} from './helpers';
import {
  EngravingOptionType,
  type OptionType,
  type SizeType,
  type StyleType,
} from './types';

import { ReactComponent as CloseSvg } from '@/assets/icons/close-icon.svg';

import * as S from './EngravingModal.styles';

const isInitialsEngravingTooShort = (
  isWithSeparators: boolean,
  engravingValue: string,
): boolean =>
  (isWithSeparators && engravingValue.length < 3) ||
  (!isWithSeparators && engravingValue.length < 2);

const isMessageValidationBlocking = (
  validationError: MessageEngravingValidationError,
): boolean =>
  validationError === 'largeTooLong' ||
  validationError === 'invalidCharacters' ||
  validationError === 'cursiveAmpersandNotSupported';

const getMessageSubmitValidationError = (
  validationError: MessageEngravingValidationError,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): GlobalError => ({
  message:
    validationError === 'empty'
      ? formatMessage(formMessages.requiredField)
      : formatMessage(engravingMessages.messageTooLongMedium),
});

const getMessageCurrentErrors = (
  messageError: string | undefined,
  messageValidationError: MessageEngravingValidationError | null,
  errors: GlobalError,
): GlobalError => {
  if (messageError) {
    return { message: messageError };
  }
  if (messageValidationError === 'empty' && errors.message) {
    return errors;
  }
  return {};
};

const getCurrentErrors = (
  optionSelected: OptionType,
  messageError: string | undefined,
  messageValidationError: MessageEngravingValidationError | null,
  errors: GlobalError,
): GlobalError => {
  if (optionSelected !== EngravingOptionType.Message) {
    return errors;
  }
  return getMessageCurrentErrors(messageError, messageValidationError, errors);
};

const hasEngravingCharacterInputError = (
  optionSelected: OptionType,
  messageValidationError: MessageEngravingValidationError | null,
  errors: GlobalError,
): boolean => {
  if (optionSelected === EngravingOptionType.Message) {
    return (
      messageValidationError !== null && messageValidationError !== 'empty'
    );
  }
  return Boolean(errors.message);
};

const getCharacterGuidanceMessage = (
  optionSelected: OptionType,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string => {
  if (optionSelected === EngravingOptionType.Message) {
    return formatMessage(engravingMessages.messageSizeGuidance);
  }
  if (optionSelected === EngravingOptionType.Initials) {
    return formatMessage(engravingMessages.minMaxInitialsCharacters);
  }
  return formatMessage(engravingMessages.minMaxDateCharacters);
};

export interface EngravingModalProps extends ModalProps {
  lineId?: string;
  product?: ProductVariantLineFragment['product'];
  variantCTA?: OrderAppCtaType;
  variantId?: string;
}

export const EngravingModal = ({
  isOpen,
  lineId,
  onClose,
  product,
  variantCTA,
  variantId,
  ...props
}: EngravingModalProps) => {
  const {
    lineAdd,
    lineDelete,
    snapshot: { isProcessing, notification },
  } = useCheckout();
  const { formatMessage } = useIntl();
  const [errors, setErrors] = useState<GlobalError>({});
  const { sendEvent } = useGoogleAnalytics();
  const [optionSelected, setOptionSelected] = useState<OptionType>(
    EngravingOptionType.Message,
  );
  const [styleSelected, setStyleSelected] = useState<StyleType>('Block');
  const [sizeSelected, setSizeSelected] = useState<SizeType>('M');
  const [engravingValue, setEngravingValue] = useState<string>('');
  const [isWithSeparators, setIsWithSeparators] = useState(false);
  const [topError, setTopError] = useState<string>('');
  const hasRealErrorRef = useRef(false);

  const genericErrorMessage = formatMessage(
    engravingMessages.engravingUnavailable,
  );

  const resetState = () => {
    setOptionSelected(EngravingOptionType.Message);
    setStyleSelected('Block');
    setSizeSelected('M');
    setEngravingValue('');
    setIsWithSeparators(false);
    resetErrors();
  };

  useEffect(() => {
    if (isOpen) {
      sendEvent({
        event: EVENT_NAME.POPIN_VIEW,
        popin_title: popinTitle.engraving,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (notification && isErrorNotification(notification)) {
      notification.map((error) => {
        const message = getFormattedMessage(
          error,
          formatMessage,
          mapApiErrorCode,
        );
        if (message) {
          hasRealErrorRef.current = true;
          setTopError(message);
        }
      });
    }
  }, [notification]);

  if (!product) return null;

  const backEngravingMedia = product?.media?.find(
    (media) => media.backEngravingCoordinates !== null,
  );

  const resetErrors = () => {
    setErrors({});
    setTopError('');
    hasRealErrorRef.current = false;
  };

  // This will set the top error only if no real error has been set by the notification
  const handleTopError = (error: string) => {
    if (!hasRealErrorRef.current) {
      setTopError(error);
    }
  };

  const handleModalClose = () => {
    sendEvent({
      event: EVENT_NAME.POPIN_CLOSE,
      popin_title: popinTitle.engraving,
    });
    resetState();

    onClose?.();
  };

  const changeOption = (option: OptionType) => {
    setOptionSelected(option);
  };

  const messageValidationError =
    optionSelected === EngravingOptionType.Message
      ? validateMessageEngraving(engravingValue, sizeSelected, styleSelected)
      : null;
  const showLargeTooLongError = messageValidationError === 'largeTooLong';
  const showCursiveAmpersandError =
    messageValidationError === 'cursiveAmpersandNotSupported';
  const showInvalidCharactersError =
    messageValidationError === 'invalidCharacters';
  const messageError =
    messageValidationError === 'mediumTooLong'
      ? formatMessage(engravingMessages.messageTooLongMedium)
      : undefined;
  const currentErrors = getCurrentErrors(
    optionSelected,
    messageError,
    messageValidationError,
    errors,
  );
  const hasCharacterInputError = hasEngravingCharacterInputError(
    optionSelected,
    messageValidationError,
    errors,
  );
  const isAddToBagDisabled =
    optionSelected === EngravingOptionType.Message &&
    messageValidationError !== null &&
    messageValidationError !== 'empty';
  const engravingPreviewValue =
    optionSelected === EngravingOptionType.Message
      ? getMessageEngravingPreviewValue(engravingValue, sizeSelected)
      : engravingValue;
  const characterGuidanceMessage = getCharacterGuidanceMessage(
    optionSelected,
    formatMessage,
  );

  const validateInputBeforeSubmit = (): boolean => {
    if (optionSelected === EngravingOptionType.Message) {
      const validationError = validateMessageEngraving(
        engravingValue,
        sizeSelected,
        styleSelected,
      );
      if (!validationError) return true;
      if (isMessageValidationBlocking(validationError)) return false;

      setErrors(
        getMessageSubmitValidationError(validationError, formatMessage),
      );
      return false;
    }

    if (
      optionSelected === EngravingOptionType.Initials &&
      isInitialsEngravingTooShort(isWithSeparators, engravingValue)
    ) {
      setErrors({
        message: formatMessage(engravingMessages.charactersMinLength),
      });
      return false;
    }

    if (optionSelected === EngravingOptionType.Date) {
      const result = validateEngravingDate(engravingValue);
      if (!result.valid) {
        setErrors({
          message: result.message ? formatMessage(result.message) : undefined,
        });
        return false;
      }
    }

    return true;
  };

  const getParsedCoordinates = () => {
    const engravingCoordinates = sanitizeCoordinates(
      backEngravingMedia?.backEngravingCoordinates ?? '',
    );

    if (!engravingCoordinates) return null;
    return parseCoordinates(engravingCoordinates);
  };

  const resolveWatchLineId = async (): Promise<string | null> => {
    if (lineId) return lineId;
    if (!variantCTA || !variantId) return null;

    try {
      const lines = await lineAdd(variantId, variantCTA);
      return (lines as CheckoutLineFragment[])[0]?.id ?? null;
    } catch {
      handleTopError(genericErrorMessage);
      return null;
    }
  };

  const addEngravingLine = async (
    watchLineId: string,
    metadata: OrderAppMetadataInput,
  ) => {
    const safeDeleteWatchLine = async () => {
      try {
        await lineDelete(watchLineId);
      } catch {
        // Best-effort cleanup; caller displays the user-facing error.
      }
    };

    try {
      const lineData = await lineAdd(
        ENGRAVING_VARIANT_ID,
        'ORDER',
        watchLineId,
        [metadata],
      );

      if ((lineData as OrderAppError[]).some((line) => line.message)) {
        await safeDeleteWatchLine();
        return false;
      }

      return true;
    } catch {
      await safeDeleteWatchLine();
      return false;
    }
  };

  const handleAddToBag = async () => {
    if (!validateInputBeforeSubmit()) return;

    const parsedCoordinates = getParsedCoordinates();
    if (!parsedCoordinates) {
      handleTopError(genericErrorMessage);
      return;
    }

    const metadata = {
      key: 'item.engraving_details',
      value: JSON.stringify({
        type: optionSelected,
        style: styleSelected,
        size: sizeSelected,
        text: engravingValue,
        coordinates: parsedCoordinates,
      }),
    };

    const watchLineId = await resolveWatchLineId();
    if (!watchLineId) {
      handleTopError(genericErrorMessage);
      return;
    }

    const lineCreated = await addEngravingLine(watchLineId, metadata);
    if (!lineCreated) {
      handleTopError(genericErrorMessage);
      return;
    }

    handleModalClose();
  };

  const handleSelectSizeM = () => {
    setSizeSelected('M');
    resetErrors();
  };

  const renderCharacterInputError = () => {
    if (showLargeTooLongError) {
      return (
        <ErrorMessage data-test-id='EngravingModalBodyErrorMessage'>
          <FormattedMessage
            {...engravingMessages.messageTooLongLarge}
            values={{
              largeLimit: (
                <strong>
                  <FormattedMessage
                    {...engravingMessages.messageLargeCharacterLimit}
                  />
                </strong>
              ),
              mediumLimit: (
                <strong>
                  <FormattedMessage
                    {...engravingMessages.messageMediumCharacterLimit}
                  />
                </strong>
              ),
              selectFontMAction: (
                <S.SelectSizeButton onClick={handleSelectSizeM} type='button'>
                  <FormattedMessage {...engravingMessages.messageSelectFontM} />
                </S.SelectSizeButton>
              ),
            }}
          />
        </ErrorMessage>
      );
    }

    if (showCursiveAmpersandError) {
      return (
        <ErrorMessage data-test-id='EngravingModalBodyErrorMessage'>
          <FormattedMessage
            {...engravingMessages.messageCursiveAmpersandNotSupported}
          />
        </ErrorMessage>
      );
    }

    if (showInvalidCharactersError) {
      if (styleSelected === 'Cursive') {
        return (
          <ErrorMessage data-test-id='EngravingModalBodyErrorMessage'>
            <FormattedMessage
              {...engravingMessages.messageInvalidCharactersCursive}
            />
          </ErrorMessage>
        );
      }

      return (
        <ErrorMessage data-test-id='EngravingModalBodyErrorMessage'>
          <FormattedMessage
            {...engravingMessages.messageInvalidCharacters}
            values={{
              ampersandSymbol: (
                <strong>
                  <FormattedMessage
                    {...engravingMessages.messageInvalidCharactersAmpersand}
                  />
                </strong>
              ),
              lettersRange: (
                <strong>
                  <FormattedMessage
                    {...engravingMessages.messageInvalidCharactersLettersRange}
                  />
                </strong>
              ),
              numbersRange: (
                <strong>
                  <FormattedMessage
                    {...engravingMessages.messageInvalidCharactersNumbersRange}
                  />
                </strong>
              ),
            }}
          />
        </ErrorMessage>
      );
    }

    return (
      <InputErrorMessage
        data-test-id='EngravingModalBodyErrorMessage'
        error={currentErrors}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      maxWidth='md'
      onClose={handleModalClose}
      {...props}
      data-testid='EngravingModal'
    >
      <Modal.Header data-test-id='EngravingModalHeader' divider>
        <SectionHeader
          data-test-id='EngravingModalHeaderSectionHeader'
          sizeDesktop={40}
          sizeMobile={34}
          text={formatMessage(serviceMessages.engraving)}
          textAlign='center'
        />
        <S.CloseIconWrapper
          aria-label='Close engraving modal'
          data-testid='EngravingModalCloseIcon'
          onClick={handleModalClose}
        >
          <CloseSvg />
        </S.CloseIconWrapper>
      </Modal.Header>
      <Modal.Body data-test-id='EngravingModalBody'>
        <S.Wrapper data-test-id='EngravingModalBodyFlex'>
          <S.WatchWrapper>
            <EngravingImage
              engravingValue={engravingPreviewValue}
              product={product}
              sizeSelected={sizeSelected}
              styleSelected={styleSelected}
            />
            <S.WatchName>{product.name}</S.WatchName>
          </S.WatchWrapper>
          <S.ContentWrapper data-test-id='EngravingModalBodyFlex'>
            {topError && (
              <InputErrorMessage
                data-test-id='EngravingModalBodyGenericErrorMessage'
                error={{
                  message: topError,
                }}
              />
            )}
            {/* TITLE */}
            <S.Text
              color='primaryBlue'
              data-test-id='EngravingModalBodyTypography'
              pb={1}
            >
              {formatMessage(engravingMessages.description)}
            </S.Text>

            {/* OPTION */}
            <S.GroupWrapper>
              <S.Text
                color='primaryBlue'
                data-test-id='EngravingModalBodyTypography'
                scale='h5'
              >
                {formatMessage(engravingMessages.optionTitle)}
              </S.Text>
              <S.OptionSectionWrapper data-testid='engravingOptionSection'>
                <Radio
                  checked={optionSelected === EngravingOptionType.Message}
                  data-testid='radioMessage'
                  name='engravingOption'
                  onChange={() => {
                    changeOption(EngravingOptionType.Message);
                    resetErrors();
                  }}
                  value={EngravingOptionType.Message}
                >
                  {formatMessage(engravingMessages.optionMessage)}
                </Radio>

                <S.OptionSecondaryRow>
                  <Radio
                    checked={optionSelected === EngravingOptionType.Initials}
                    data-testid='radioInitials'
                    name='engravingOption'
                    onChange={() => {
                      changeOption(EngravingOptionType.Initials);
                      resetErrors();
                    }}
                    value={EngravingOptionType.Initials}
                  >
                    {formatMessage(engravingMessages.optionInitials)}
                  </Radio>

                  <Radio
                    checked={optionSelected === EngravingOptionType.Date}
                    data-testid='radioDate'
                    name='engravingOption'
                    onChange={() => {
                      changeOption(EngravingOptionType.Date);
                      resetErrors();
                    }}
                    value={EngravingOptionType.Date}
                  >
                    {formatMessage(engravingMessages.optionDate)}
                  </Radio>
                </S.OptionSecondaryRow>
              </S.OptionSectionWrapper>
            </S.GroupWrapper>

            {/* ENTER THE CHARACTERS */}
            <S.GroupWrapper>
              <S.Text
                color='primaryBlue'
                data-test-id='EngravingModalBodyTypography'
                scale='h5'
              >
                {formatMessage(engravingMessages.enterTheCharactersTitle)}
              </S.Text>
              <S.Text
                color='secondaryGrey'
                data-test-id='EngravingModalBodyTypography'
                scale='p2'
              >
                {characterGuidanceMessage}
              </S.Text>
              <Flex flexDirection='column' gridGap={1} pb={1}>
                <EngravingInputs
                  engravingValue={engravingValue}
                  hasError={hasCharacterInputError}
                  isWithSeparators={isWithSeparators}
                  resetErrors={resetErrors}
                  setEngravingValue={setEngravingValue}
                  setIsWithSeparators={setIsWithSeparators}
                  style={styleSelected}
                  type={optionSelected}
                />
              </Flex>
              {renderCharacterInputError()}
            </S.GroupWrapper>

            {/* STYLE */}
            <S.GroupWrapper>
              <S.Text
                color='primaryBlue'
                data-test-id='EngravingModalBodyTypography'
                scale='h5'
              >
                {formatMessage(engravingMessages.styleTitle)}
              </S.Text>
              <S.SectionWrapper>
                <S.StyleButton
                  $selected={styleSelected === 'Block'}
                  $style='Block'
                  onClick={() => {
                    setStyleSelected('Block');
                  }}
                >
                  {formatMessage(engravingMessages.styleBlock)}
                </S.StyleButton>

                <S.StyleButton
                  $selected={styleSelected === 'Cursive'}
                  $style='Cursive'
                  onClick={() => {
                    setStyleSelected('Cursive');
                  }}
                >
                  {formatMessage(engravingMessages.styleCursive)}
                </S.StyleButton>
              </S.SectionWrapper>
            </S.GroupWrapper>

            {/* SIZE */}
            <S.GroupWrapper>
              <S.Text
                color='primaryBlue'
                data-test-id='EngravingModalBodyTypography'
                scale='h5'
              >
                {formatMessage(engravingMessages.sizeTitle)}
              </S.Text>
              <S.SectionWrapper>
                <S.StyleButton
                  $selected={sizeSelected === 'M'}
                  $size='small'
                  onClick={() => {
                    setSizeSelected('M');
                    resetErrors();
                  }}
                >
                  M
                </S.StyleButton>

                <S.StyleButton
                  $selected={sizeSelected === 'L'}
                  $size='small'
                  onClick={() => {
                    setSizeSelected('L');
                    resetErrors();
                  }}
                >
                  L
                </S.StyleButton>
              </S.SectionWrapper>
            </S.GroupWrapper>

            {/* TOOLTIP */}
            <S.GroupWrapper>
              <S.Text
                color='primaryBlue'
                data-test-id='EngravingModalBodyTypography'
                pb={1}
              >
                <FormattedMessage
                  {...engravingMessages.tooltip}
                  values={{
                    conditionsOfSaleLink: (
                      <LinkWithLocale
                        href={paths.termsAndCondition.url()}
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        <FormattedMessage
                          {...engravingMessages.conditionsOfSale}
                        />
                      </LinkWithLocale>
                    ),
                    faqLink: (
                      <LinkWithLocale
                        href={paths.service.faq.url()}
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        <FormattedMessage {...engravingMessages.faq} />
                      </LinkWithLocale>
                    ),
                  }}
                />
              </S.Text>

              {/* ADD TO BAG BUTTON */}
              <Button
                data-testid='AddToBagEngravingModalButton'
                disabled={isAddToBagDisabled}
                isProcessing={isProcessing}
                onClick={handleAddToBag}
                style={{ marginTop: '24px' }}
              >
                {formatMessage(commonMessages.addToBag)}
              </Button>
            </S.GroupWrapper>
          </S.ContentWrapper>
        </S.Wrapper>
      </Modal.Body>
    </Modal>
  );
};
